import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAppContext } from '../../../context/AppContext';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Alert from '@mui/material/Alert';
import Grow from '@mui/material/Grow';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import DeleteIcon from '@mui/icons-material/Delete';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RecipePickerModal from '../RecipePickerModal/RecipePickerModal';
import './AddMealModal.css';
import TourButton from '../../TourButton/TourButton';
import { getAddMealModalSteps } from '../../../tours/addMealModal';
import { useMediaQuery } from '@mui/material';

const AddMealModal = ({ dietPlan, setDietPlan, editedDiet, handleEditedDiet, setOriginalDietFoodProportion, selectedDay, setShowModal }) => {
    const { t } = useTranslation();
    const { currentLanguage, dietist } = useAppContext();
    const { appColors } = useAppContext();
    const tourSteps = useMemo(() => getAddMealModalSteps(t), [t]);
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [mealSelect, setMealSelect] = useState("");
    const [mealSelectOptions, setMealSelectOptions] = useState([]);
    const [mealSelectError, setMealSelectError] = useState(false); // used for validation
    const [alertsStates, setAlertsStates] = useState({
        mealSelect: false,
        emptyTable: false,
        tableCreation: false,
    });
    const [foodList, setFoodList] = useState({});
    const [baseMacrosFoodProportion, setBaseMacrosFoodProportion] = useState({}); // used to create the food proportions after the food is inserted
    const [foodCounter, setFoodCounter] = useState(0); // used to track the food in the foodList
    const [foodSearchList, setFoodSearchList] = useState([]);
    const [mealTotals, setMealTotals] = useState({ kcal: 0, fats: 0, proteins: 0, carbs: 0, sugars: 0, fibers: 0, salt: 0 });
    const [showRecipePicker, setShowRecipePicker] = useState(false);
    const [onlyGenerics, setOnlyGenerics] = useState(true); // filter food search to generic brand only

    useEffect(() => {
        const createMealSelectOptions = () => {
            let options = new Set(["breakfast", "snack1", "lunch", "snack2", "dinner", "snack3"]);
            for (const key of Object.keys(dietPlan[selectedDay])) {
                options.delete(key);
            }
            setMealSelectOptions([...options]);
        }

        if (dietPlan && dietPlan !== undefined) {
            createMealSelectOptions();
        }
    }, []);

    useEffect(() => {
        const calculateMealTotals = () => {
            let totals = { kcal: 0, fats: 0, proteins: 0, carbs: 0, sugars: 0, fibers: 0, salt: 0 };
    
            for (const value of Object.values(foodList)) {
                totals.kcal     += Number(parseFloat(value.kcal).toFixed(1));
                totals.fats     += Number(parseFloat(value.fats).toFixed(1));
                totals.proteins += Number(parseFloat(value.proteins).toFixed(1));
                totals.carbs    += Number(parseFloat(value.carbs).toFixed(1));
                totals.sugars   += Number(parseFloat(value.sugars ?? 0).toFixed(1));
                totals.fibers   += Number(parseFloat(value.fibers ?? 0).toFixed(1));
                totals.salt     += Number(parseFloat(value.salt   ?? 0).toFixed(1));
            }
            setMealTotals(totals);
        };
    
        if (foodList && Object.keys(foodList).length > 0) {
            calculateMealTotals();
        }
    }, [foodList]); // Runs every time foodList changes

    const getFoodsFilteredByName = async (foodName) => {
        let url = `${process.env.REACT_APP_BACKEND_URL}/foods/?nameFilter=${foodName}`;
        url += `&language=${currentLanguage}`;
        if (onlyGenerics) url += '&onlyGenerics=true';
        let response = await axios.get(url, { withCredentials: true });
        setFoodSearchList(response.data);
    }

    const generateSelectMealOptions = () => {
        let options = [];
        
        for (let meal of mealSelectOptions) {
            options.push(
                <MenuItem key={meal} value={meal}>{t(meal)}</MenuItem>
            );
        }

        return options;
    }

    const handleMealSelectChange = (e) => {
        let meal = e.target.value;
        setMealSelect(meal);
        setMealSelectError(false);
    }

    // Add a new empty food table row
    const createNewFoodTableRow = () => {
        const newFoodRow = {
            name: "",
            quantity: 0,
            kcal: 0,
            fats: 0,
            proteins: 0,
            carbs: 0,
        };
        setFoodList((prevFoodList) => ({
            ...prevFoodList,
            [foodCounter]: newFoodRow, // Use foodCounter as a dynamic key
        }));

        setBaseMacrosFoodProportion((prevProportion) => ({
            ...prevProportion,
            [foodCounter]: newFoodRow, // Initialize base proportions with the same values
        }));
    
        setFoodCounter((prevCounter) => prevCounter + 1); // Increment the foodCounter
        };

    const createMealEntryFromRecipe = (recipe) => ({
        name: recipe?.name ?? { en: '', it: '' },
        quantity: recipe?.servings ?? 1,
        kcal: Number(recipe?.kcal ?? 0),
        fats: Number(recipe?.fats ?? 0),
        proteins: Number(recipe?.proteins ?? 0),
        carbs: Number(recipe?.carbs ?? 0),
        sugars: Number(recipe?.sugars ?? 0),
        fibers: Number(recipe?.fibers ?? 0),
        salt: Number(recipe?.salt ?? 0),
        recipeId: recipe?._id,
        recipeImageUrl: recipe?.imageUrl ?? recipe?.image ?? '',
        servings: Number(recipe?.servings ?? 1),
        preparationTime: recipe?.preparationTime ?? '',
        description: recipe?.description ?? '',
        type: 'recipe',
    });

    const handleSelectRecipe = (recipe) => {
        const recipeMealEntry = createMealEntryFromRecipe(recipe);

        setFoodList((prevFoodList) => ({
            ...prevFoodList,
            [foodCounter]: recipeMealEntry,
        }));

        setBaseMacrosFoodProportion((prevProportion) => ({
            ...prevProportion,
            [foodCounter]: recipeMealEntry,
        }));

        setFoodCounter((prevCounter) => prevCounter + 1);
        setShowRecipePicker(false);
    };
    

    const deleteFood = (index) => {
        let foodListCopy = {...foodList};
        delete foodListCopy[index];
        setFoodList(foodListCopy);
    }

    const handleFoodOptionSelect = (event, value) => {
        if (value) {
            const selectedFood = typeof value === 'object'
                ? value
                : foodSearchList.find((food) => food.name[currentLanguage] === value);
            if (selectedFood) {
                setFoodList((prevFoodList) => ({
                    ...prevFoodList,
                    [event.target.id.split("-")[0]]: selectedFood,
                }));
                setBaseMacrosFoodProportion((prevFoodList) => ({
                    ...prevFoodList,
                    [event.target.id.split("-")[0]]: selectedFood,
                }));
            }
            setFoodSearchList([]);    
        }
    };
        
    // Update food data on input change
    const handleFoodChange = (index, key, value) => {
        setFoodList((prevFoodList) => {
            let updatedFood = { ...prevFoodList[index], [key]: value };
    
            // Check if base proportions need to be initialized
            if (key === "quantity") {
                setBaseMacrosFoodProportion((prevProportion) => {
                    const base = prevProportion[index];
    
                    // If all values are 0, updates the quantity quantity
                    if (base && base.kcal === 0 && base.fats === 0 && base.proteins === 0 && base.carbs === 0) {
                        return {
                            ...prevProportion,
                            [index]: {
                                quantity: value,
                                kcal: base.kcal || 0,
                                fats: base.fats || 0,
                                proteins: base.proteins || 0,
                                carbs: base.carbs || 0,
                            },
                        };
                    }
    
                    return prevProportion;
                });
    
                // Recalculate other macros based on proportions
                const base = baseMacrosFoodProportion[index];
                if (base) {
                    const ratio = value / (base.quantity || 1); // Avoid division by zero
                    updatedFood.kcal = Number((base.kcal * ratio).toFixed(1));
                    updatedFood.fats = Number((base.fats * ratio).toFixed(1));
                    updatedFood.proteins = Number((base.proteins * ratio).toFixed(1));
                    updatedFood.carbs = Number((base.carbs * ratio).toFixed(1));
                }
            }
    
            return {
                ...prevFoodList,
                [index]: updatedFood,
            };
        });
    
        // If a macronutrient is being updated, update the base proportion as well
        if (key !== "quantity") {
            setBaseMacrosFoodProportion((prevProportion) => ({
                ...prevProportion,
                [index]: {
                    ...prevProportion[index],
                    [key]: value,
                },
            }));
        }
    };

    const validate = () => {
        let valid = true;

        // check meal select
        if (mealSelect === "") {
            setMealSelectError(true);
            setAlertsStates((prev) => ({ ...prev, mealSelect: true }));
            valid = false;

            // Automatically turn off after 3 seconds
            setTimeout(() => {
                setAlertsStates((prev) => ({ ...prev, mealSelect: false }));
            }, 3000);
        }

        // check the food list is not empty
        if (Object.keys(foodList).length === 0) {
            setAlertsStates((prev) => ({ ...prev, emptyTable: true }));
            valid = false;

            // Automatically turn off after 3 seconds
            setTimeout(() => {
                setAlertsStates((prev) => ({ ...prev, emptyTable: false }));
            }, 3000);
        }

        return valid;
    }
        
    const handleSubmit = () => {
        if (validate()) {
            let newMeal = {
                kcal:     mealTotals.kcal,
                fats:     mealTotals.fats,
                proteins: mealTotals.proteins,
                carbs:    mealTotals.carbs,
                sugars:   mealTotals.sugars,
                fibers:   mealTotals.fibers,
                salt:     mealTotals.salt,
                foods: [],
            }
            const foodArray = Object.values(foodList);
            newMeal.foods = foodArray;

            // Clone the dietPlan to maintain immutability
            let newDietPlan = { ...dietPlan };

            newDietPlan[selectedDay][mealSelect] = newMeal;

            // Recalculate daily totals
            const dailyTotals = {
                kcal: 0,
                fats: 0,
                proteins: 0,
                carbs: 0,
                sugars: 0,
                fibers: 0,
                salt: 0,
            };

            for (const mealKey of Object.keys(newDietPlan[selectedDay])) {
                if (mealKey !== 'totals') { // Skip the totals key if present
                    ['kcal', 'fats', 'proteins', 'carbs', 'sugars', 'fibers', 'salt'].forEach((macro) => {
                        dailyTotals[macro] += parseFloat(newDietPlan[selectedDay][mealKey][macro]) || 0;
                    });
                }
            }

            // Assign daily totals to the day-level 'totals' key
            newDietPlan[selectedDay].totals = dailyTotals;

            // Update the state
            setDietPlan(newDietPlan);

            setOriginalDietFoodProportion((prevDietPlan) => ({
                ...prevDietPlan,
                [selectedDay]: {
                    ...prevDietPlan[selectedDay],
                    [mealSelect]: newMeal,
                },
            }));

            // Update parent state through the callback (used in the edit diet modal)
            if (handleEditedDiet) {
                handleEditedDiet({
                    ...editedDiet,
                    dietPlan: newDietPlan,
                });
            }

            // print a success message and close the modal
            setAlertsStates((prev) => ({ ...prev, tableCreation: true }));
            setTimeout(() => {
                setAlertsStates((prev) => ({ ...prev, tableCreation: false }));
            }, 3000);
            handleClose();
        }
    };
        

    const handleClose = () => {
        // reset fields
        setMealSelect("");
        setMealSelectOptions([]);
        setMealSelectError(false);
        setAlertsStates({
            mealSelect: false,
            emptyTable: false,
            tableCreation: false,
        });
        setFoodList({});
        setBaseMacrosFoodProportion({});
        setFoodCounter(0);
        setFoodSearchList([]);
        setMealTotals({ kcal: 0, fats: 0, proteins: 0, carbs: 0 });
        setShowRecipePicker(false);

        // close the modal
        setShowModal(false);
    };

    /** Shared sx for compact numeric TextFields in the food table. */
    const numFieldSx = {
        width: '64px',
        '& .MuiOutlinedInput-root': {
            '&:hover fieldset': { borderColor: appColors.tertiary },
            '&.Mui-focused fieldset': { borderColor: appColors.secondary },
        },
        '& .MuiInputBase-input': {
            padding: '4px 8px',
            textAlign: 'right',
            fontSize: '0.85rem',
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
            '&[type=number]': { MozAppearance: 'textfield' },
        },
    };

    return (
        <>
            <div className='aml-overlay'>
                <div className='aml-modal' data-tour="aml-modal">

                    {/* ── Header ── */}
                    <div className='aml-header'>
                        <h2 className='aml-title'>{t('add-meal')}</h2>
                        <div className='aml-tutorial-btn'>
                            <TourButton steps={tourSteps} iconOnly={isMobile} label={t('tour.tutorial-btn')} />
                        </div>
                        <button className='aml-close-btn' onClick={handleClose} aria-label='Close'>
                            <span className='material-symbols-outlined'>close</span>
                        </button>
                    </div>

                    {/* ── Body ── */}
                    <div className='aml-body'>

                        {/* Meal selector */}
                        <div data-tour="aml-meal-select">
                        <FormControl
                            required
                            size='small'
                            sx={{
                                minWidth: 200,
                                maxWidth: 300,
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': { borderColor: appColors.tertiary },
                                    '&.Mui-focused fieldset': { borderColor: appColors.secondary },
                                },
                                '& .MuiInputLabel-root.Mui-focused': { color: appColors.secondary },
                            }}
                        >
                            <InputLabel id='meal-select-label'>{t('Meal')}</InputLabel>
                            <Select
                                labelId='meal-select-label'
                                id='meal-select'
                                value={mealSelect}
                                label={t('Meal') + ' *'}
                                onChange={handleMealSelectChange}
                                error={mealSelectError}
                            >
                                {generateSelectMealOptions()}
                            </Select>
                        </FormControl>
                        </div>

                        {/* Generic brand filter */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <Tooltip
                                title={t('only-generic-brand-info')}
                                arrow
                                placement='top'
                            >
                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#94a3b8', mr: 0.5, cursor: 'default' }} />
                            </Tooltip>
                            <FormControlLabel
                                control={
                                    <Switch
                                        size='small'
                                        checked={onlyGenerics}
                                        onChange={(e) => {
                                            setOnlyGenerics(e.target.checked);
                                            setFoodSearchList([]);
                                        }}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: appColors.secondary },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: appColors.secondary },
                                        }}
                                    />
                                }
                                label={<span style={{ fontSize: '0.85rem' }}>{t('only-generic-brand')}</span>}
                                sx={{ m: 0 }}
                            />
                        </Box>

                        {/* Food table */}
                        <div className='aml-table-wrap' data-tour="aml-food-table">
                            <TableContainer component={Paper} elevation={0}>
                                <Table size='small' aria-label='food table'>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className='aml-th'>{t('Food')}</TableCell>
                                            <TableCell className='aml-th' align='right'>
                                                {t('Quantity').toUpperCase()}
                                                <br /><span className='aml-th-sub'>(gr/ml)</span>
                                            </TableCell>
                                            <TableCell className='aml-th' align='right'>{t('kcal').toUpperCase()}</TableCell>
                                            <TableCell className='aml-th' align='right'>{t('Fats').toUpperCase()}</TableCell>
                                            <TableCell className='aml-th' align='right'>{t('Proteins').toUpperCase()}</TableCell>
                                            <TableCell className='aml-th' align='right'>{t('Carbs').toUpperCase()}</TableCell>
                                            <TableCell className='aml-th' />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(foodList).map(([index, food]) => (
                                            <TableRow key={index} className='aml-food-row'>
                                                <TableCell component='th' scope='row'>
                                                    <Autocomplete
                                                        id={`${index}-name`}
                                                        className='aml-food-name-input'
                                                        value={food['name'][currentLanguage] || ''}
                                                        freeSolo
                                                        onChange={(event, value) => handleFoodOptionSelect(event, value)}
                                                        onInputChange={async (_, inputValue) => {
                                                            if (!inputValue.trim()) {
                                                                setFoodSearchList([]);
                                                                return;
                                                            }
                                                            await getFoodsFilteredByName(inputValue);
                                                        }}
                                                        options={foodSearchList.filter((f) => f.name?.[currentLanguage])}
                                                        getOptionLabel={(option) => {
                                                            if (typeof option === 'string') return option;
                                                            return option.name?.[currentLanguage] || '';
                                                        }}
                                                        renderOption={(props, option) => {
                                                            const name = option.name?.[currentLanguage] || '';
                                                            const brand = option.brand;
                                                            const brandDisplay = brand
                                                                ? (typeof brand === 'object'
                                                                    ? (brand?.[currentLanguage] ?? brand?.en ?? '')
                                                                    : brand)
                                                                : null;
                                                            return (
                                                                <li {...props} key={option._id}>
                                                                    {name}
                                                                    {brandDisplay && (
                                                                        <span style={{ marginLeft: 8, fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                                                            {brandDisplay}
                                                                        </span>
                                                                    )}
                                                                </li>
                                                            );
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField {...params} size='small' />
                                                        )}
                                                        filterOptions={(x) => x}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                '&:hover fieldset': { borderColor: appColors.tertiary },
                                                                '&.Mui-focused fieldset': { borderColor: appColors.secondary },
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                                {['quantity', 'kcal', 'fats', 'proteins', 'carbs'].map((key) => (
                                                    <TableCell key={key} align='right'>
                                                        <TextField
                                                            name={`${index}-${key}`}
                                                            type='number'
                                                            value={food[key]}
                                                            onChange={(e) => handleFoodChange(index, key, parseFloat(e.target.value) || 0)}
                                                            sx={numFieldSx}
                                                        />
                                                    </TableCell>
                                                ))}
                                                <TableCell align='right'>
                                                    <div className='aml-delete-btn' onClick={() => deleteFood(index)}>
                                                        <DeleteIcon fontSize='small' />
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow className='aml-total-row'>
                                            <TableCell><b>{t('Total')}</b></TableCell>
                                            <TableCell />
                                            <TableCell align='right'><b>{Number(parseFloat(mealTotals.kcal).toFixed(1))}</b></TableCell>
                                            <TableCell align='right'><b>{Number(parseFloat(mealTotals.fats).toFixed(1))}</b></TableCell>
                                            <TableCell align='right'><b>{Number(parseFloat(mealTotals.proteins).toFixed(1))}</b></TableCell>
                                            <TableCell align='right'><b>{Number(parseFloat(mealTotals.carbs).toFixed(1))}</b></TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>

                        {/* Add food button */}
                        <div className='aml-actions-row' data-tour="aml-actions">
                            <button type='button' className='aml-add-food-btn' onClick={createNewFoodTableRow}>
                                <AddIcon sx={{ fontSize: 16 }} />
                                {t('add-new-food')}
                            </button>
                            <button type='button' className='aml-add-food-btn aml-add-recipe-btn' onClick={() => setShowRecipePicker(true)}>
                                <AddIcon sx={{ fontSize: 16 }} />
                                {t('add-new-recipe')}
                            </button>
                        </div>

                    </div>

                    {/* ── Footer ── */}
                    <div className='aml-footer' data-tour="aml-footer">
                        <button className='aml-btn aml-btn-cancel' onClick={handleClose}>{t('Cancel')}</button>
                        <button className='aml-btn aml-btn-save' onClick={handleSubmit}>{t('Confirm')}</button>
                    </div>

                </div>
            </div>

            {showRecipePicker && (
                <RecipePickerModal
                    open={showRecipePicker}
                    dietistId={dietist?._id}
                    language={currentLanguage}
                    onClose={() => setShowRecipePicker(false)}
                    onSelect={handleSelectRecipe}
                />
            )}

            {/* ── Alerts ── */}
            <Stack className='aml-alert-stack'>
                <Grow in={alertsStates.mealSelect} style={{ margin: 1 }}>
                    <Alert variant='filled' severity='error' onClose={() => setAlertsStates((prev) => ({ ...prev, mealSelect: false }))}>
                        {t('errorMessages.meal-select-required')}
                    </Alert>
                </Grow>
                <Grow in={alertsStates.emptyTable} style={{ margin: 1 }}>
                    <Alert variant='filled' severity='error' onClose={() => setAlertsStates((prev) => ({ ...prev, emptyTable: false }))}>
                        {t('errorMessages.food-table-is-empty')}
                    </Alert>
                </Grow>
                <Grow in={alertsStates.tableCreation} style={{ margin: 1 }}>
                    <Alert variant='filled' severity='success' onClose={() => setAlertsStates((prev) => ({ ...prev, tableCreation: false }))}>
                        {t('meal-added-successfully')}
                    </Alert>
                </Grow>
            </Stack>
        </>
    );
};

export default AddMealModal;
